import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';
import { ProblemAlert } from '../components/ProblemAlert.js';
import { useAuth } from '../state/auth.js';
import { useDocumentTitle } from '../lib/hooks.js';
import type { ApiProblem } from '../lib/problem.js';

/**
 * Sign in.
 *
 * ## Where the user lands afterwards
 *
 * The router passes the location the visitor was trying to reach in
 * `state.from`, so an invitation link that required a sign-in resumes at the
 * invitation rather than dumping the user on a generic dashboard. Without that,
 * every deep link in the product silently degrades into "sign in, then find it
 * yourself".
 *
 * ## Why the error is a single sentence
 *
 * The API returns `invalid_credentials` for both "no such account" and "wrong
 * password", and this form must not try to be more specific. It cannot be: the
 * whole point of the API's constant-time login is that the two are
 * indistinguishable, and a UI that guessed would undo it.
 */
export function LoginPage(): ReactNode {
  useDocumentTitle('Sign in');

  const { status, signIn, organizations } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [pending, setPending] = useState(false);

  // Already signed in — a bookmark to /login, or a back-navigation after signing
  // in. Redirecting is friendlier than showing the form again.
  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? landingFor(organizations)} replace />;
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setProblem(null);

    const result = await signIn(email.trim(), password);

    if (result.ok) {
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? landingFor(organizations), { replace: true });
      return;
    }

    setProblem(result.error);
    setPending(false);
    // The password is cleared on failure and the email is not. A wrong password
    // is the common case and retyping the address is the annoying part.
    setPassword('');
  };

  /**
   * Sign in as a seeded account with one click.
   *
   * Keystone is a permissions system, and permissions are only legible by
   * comparison: an owner can create an API key and a guest cannot. Asking a
   * visitor to sign out, sign in again as someone else, and diff the two screens
   * means almost nobody sees the thing the project is about.
   */
  const signInAs = async (demoEmail: string, demoPassword: string): Promise<void> => {
    setPending(true);
    setProblem(null);
    setEmail(demoEmail);
    setPassword(demoPassword);

    const result = await signIn(demoEmail, demoPassword);

    if (result.ok) {
      navigate(landingFor(organizations), { replace: true });
      return;
    }

    setProblem(result.error);
    setPending(false);
  };

  const fields = problem?.fieldErrors() ?? {};

  return (
    <AuthShell
      title="Sign in to Keystone"
      subtitle="Manage organisations, members, API keys and audit logs."
      footer={
        <>
          No account yet?{' '}
          <Link className="font-medium text-brand underline underline-offset-2" to="/register">
            Create one
          </Link>
        </>
      }
    >
      {problem && problem.status !== 422 ? (
        <div className="mb-4">
          <ProblemAlert problem={problem} compact />
        </div>
      ) : null}

      <form onSubmit={(event) => void onSubmit(event)} noValidate className="flex flex-col gap-4">
        <Field label="Email address" error={fields.email} required>
          {(props) => (
            <Input
              {...props}
              type="email"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              autoFocus
              required
              placeholder="you@example.com"
            />
          )}
        </Field>

        <Field label="Password" error={fields.password} required>
          {(props) => (
            <Input
              {...props}
              type="password"
              name="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
          )}
        </Field>

        <Button type="submit" loading={pending} className="mt-1 w-full">
          Sign in
        </Button>
      </form>

      <SeedAccounts onSignIn={signInAs} busy={pending} />
    </AuthShell>
  );
}

/**
 * The seeded accounts, with one-click sign-in.
 *
 * ## Why this is not gated behind `import.meta.env.DEV`
 *
 * It was, and the reasoning was that a development-only branch is removed from
 * the production bundle by the bundler rather than merely hidden at runtime — so
 * the credentials would never ship.
 *
 * That reasoning is sound and the conclusion was wrong, because the premise is
 * wrong: the credentials are already in `apps/api/seeds/seed.sql`, which is
 * committed. Gating the UI hid them from the only people they were written for —
 * visitors to the deployed demo — while protecting nothing. The deployed console
 * showed a bare sign-in form with no indication that any account existed.
 *
 * So they are shown everywhere, and they are shown with a button rather than as
 * text to retype. The security posture is unchanged: these accounts exist only on
 * a demonstration deployment, and anyone can read them from the repository.
 */
function SeedAccounts({
  onSignIn,
  busy,
}: {
  onSignIn: (email: string, password: string) => Promise<void>;
  busy: boolean;
}): ReactNode {
  /*
   * Each seeded account has its own password, and that is deliberate rather than
   * incidental: sharing one password across four roles would let a reader assume
   * the roles differ only by a column value, when the point is that they differ
   * in what they are permitted to do. The seed mirrors the API's own behaviour,
   * where a password is per user.
   */
  const accounts = [
    {
      email: 'owner@keystone.test',
      password: 'keystone-owner-2026',
      role: 'Owner of Acme Labs',
      can: 'everything, including deleting the organisation',
    },
    {
      email: 'admin@keystone.test',
      password: 'keystone-admin-2026',
      role: 'Admin of Acme Labs',
      can: 'members, invitations, API keys and the audit log',
    },
    {
      email: 'member@keystone.test',
      password: 'keystone-member-2026',
      role: 'Member of Acme Labs',
      can: 'read the organisation, but not the audit log',
    },
    {
      email: 'guest@keystone.test',
      password: 'keystone-guest-2026',
      role: 'Guest of Northwind Traders',
      can: 'read only, and only in the other organisation',
    },
  ];

  return (
    <div className="mt-5 rounded-lg border border-line bg-surface-2 p-3.5">
      <p className="text-xs font-medium text-ink">Try it without registering</p>
      <p className="mt-1 text-xs leading-relaxed text-ink-muted">
        Four accounts in two organisations, each with a different role. Sign in as two
        of them to see how the same screens change.
      </p>

      <ul className="mt-3 flex flex-col gap-2">
        {accounts.map((account) => (
          <li key={account.email}>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onSignIn(account.email, account.password)}
              className="w-full rounded-md border border-line bg-canvas px-2.5 py-2 text-left transition-colors hover:bg-surface-3 disabled:opacity-50"
            >
              <span className="block text-xs font-medium text-ink">{account.role}</span>
              <span className="block text-[11px] text-ink-subtle">{account.can}</span>
              <span className="break-anywhere mt-0.5 block font-mono text-[10px] text-ink-subtle">
                {account.email} / {account.password}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <p className="mt-3 text-[11px] leading-relaxed text-ink-subtle">
        The accounts come from <code className="rounded bg-surface-3 px-1 py-0.5">seeds/seed.sql</code>,
        which is in the repository, and each has its own password.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Register                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Create an account.
 *
 * The password rules are enforced client-side by importing the *same* Zod schema
 * the API validates with, so the form cannot accept something the server will
 * reject. That is the reason `@keystone/shared` exists as a package rather than
 * as a copy of the schemas in each app.
 *
 * The server is still the authority. This is a courtesy that saves a round trip,
 * not a security control — anyone can bypass it with a `curl`.
 */
export function RegisterPage(): ReactNode {
  useDocumentTitle('Create an account');

  const { status, signUp, organizations } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [problem, setProblem] = useState<ApiProblem | null>(null);
  const [pending, setPending] = useState(false);

  if (status === 'authenticated') return <Navigate to={landingFor(organizations)} replace />;

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setProblem(null);

    const result = await signUp({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
    });

    if (result.ok) {
      // A brand-new account belongs to nothing, so it goes straight to the
      // create-organisation flow rather than to an empty dashboard.
      navigate('/orgs/new', { replace: true });
      return;
    }

    setProblem(result.error);
    setPending(false);
  };

  const fields = problem?.fieldErrors() ?? {};
  const update = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <AuthShell
      title="Create your Keystone account"
      subtitle="One account, as many organisations as you need."
      footer={
        <>
          Already have an account?{' '}
          <Link className="font-medium text-brand underline underline-offset-2" to="/login">
            Sign in
          </Link>
        </>
      }
    >
      {problem && problem.status !== 422 ? (
        <div className="mb-4">
          <ProblemAlert problem={problem} compact />
        </div>
      ) : null}

      <form onSubmit={(event) => void onSubmit(event)} noValidate className="flex flex-col gap-4">
        <Field label="Name" error={fields.name} required>
          {(props) => (
            <Input
              {...props}
              type="text"
              value={form.name}
              onChange={(event) => update('name')(event.target.value)}
              autoComplete="name"
              autoFocus
              required
              placeholder="Ada Lovelace"
            />
          )}
        </Field>

        <Field label="Email address" error={fields.email} required>
          {(props) => (
            <Input
              {...props}
              type="email"
              value={form.email}
              onChange={(event) => update('email')(event.target.value)}
              autoComplete="username"
              required
              placeholder="you@example.com"
            />
          )}
        </Field>

        <Field
          label="Password"
          error={fields.password}
          hint="At least 12 characters. Length matters far more than symbols."
          required
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              value={form.password}
              onChange={(event) => update('password')(event.target.value)}
              autoComplete="new-password"
              required
            />
          )}
        </Field>

        <Button type="submit" loading={pending} className="mt-1 w-full">
          Create account
        </Button>
      </form>

      <div className="mt-4">
        <Alert tone="info">
          Registration is rate limited to 10 attempts per minute per IP address. The API deliberately
          does not hide whether an address is already registered — doing so would make the flow
          unusable. See <code>docs/SECURITY.md</code>.
        </Alert>
      </div>
    </AuthShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Shared shell                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The centred card the three anonymous pages share.
 *
 * A separate component rather than a layout route because the invitation page
 * also needs it and is not nested under `/login`. Sharing the markup keeps the
 * three pages visually identical, which is what makes a sign-up flow feel like
 * one thing.
 */
export function AuthShell({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}): ReactNode {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2">
          <span
            aria-hidden="true"
            className="flex size-8 items-center justify-center rounded-lg bg-brand text-base font-bold text-brand-ink"
          >
            K
          </span>
          <span className="text-base font-semibold tracking-tight text-ink">Keystone</span>
        </div>

        <Card className="p-5 sm:p-6">
          <h1 className="text-base font-semibold tracking-tight text-ink">{title}</h1>
          {subtitle ? <p className="mt-1 text-[0.8125rem] text-ink-muted">{subtitle}</p> : null}
          <div className="mt-5">{children}</div>
        </Card>

        {footer ? <p className="mt-4 text-center text-[0.8125rem] text-ink-muted">{footer}</p> : null}
      </div>
    </div>
  );
}

/** Where to send someone who has just authenticated. */
export function landingFor(organizations: { slug: string }[]): string {
  const first = organizations[0];
  return first ? `/orgs/${first.slug}` : '/orgs/new';
}
